import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Search, Pencil, Trash2, FileSpreadsheet, TrendingUp, TrendingDown } from "lucide-react";
import { formatCurrency, formatPercent } from "@/lib/utils";
import { API } from "@/lib/api";


export default function CostSheets() {
  const [sheets, setSheets] = useState([]);
  const [calculations, setCalculations] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);

  useEffect(() => {
    fetchSheets();
  }, []);

  const fetchSheets = async () => {
    try {
      const res = await axios.get(`${API}/cost-sheets`);
      setSheets(res.data);
      
      // Calculate pricing for each sheet
      const calcs = {};
      for (const sheet of res.data) {
        try {
          const calcRes = await axios.get(`${API}/cost-sheets/${sheet.id}/calculate`);
          calcs[sheet.id] = calcRes.data;
        } catch (e) {
          console.error(`Error calculating for sheet ${sheet.id}:`, e);
        }
      }
      setCalculations(calcs);
    } catch (error) {
      toast.error("Erro ao carregar fichas de custo");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSheet) return;

    try {
      await axios.delete(`${API}/cost-sheets/${selectedSheet.id}`);
      toast.success("Ficha de custo excluída com sucesso");
      setDeleteDialogOpen(false);
      setSelectedSheet(null);
      fetchSheets();
    } catch (error) {
      toast.error("Erro ao excluir ficha de custo");
    }
  };

  const filteredSheets = sheets.filter((sheet) => {
    const matchesSearch = sheet.name.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === "all" || sheet.service_type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="cost-sheets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Fichas de Custo
          </h1>
          <p className="text-slate-500 mt-1">Gerencie as fichas de custo dos serviços e produtos</p>
        </div>
        <Link to="/cost-sheets/new">
          <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-cost-sheet-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nova Ficha
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar ficha de custo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-sheets"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-type">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="servico">Serviço</SelectItem>
                <SelectItem value="produto">Produto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Cost Sheets Table */}
      <Card>
        <CardContent className="p-0">
          {filteredSheets.length === 0 ? (
            <div className="empty-state py-12">
              <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {search || typeFilter !== "all"
                  ? "Nenhuma ficha encontrada"
                  : "Nenhuma ficha de custo cadastrada"}
              </p>
              {!search && typeFilter === "all" && (
                <Link to="/cost-sheets/new">
                  <Button className="mt-4 bg-teal-600 hover:bg-teal-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Criar primeira ficha
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Preço Venda</TableHead>
                  <TableHead className="text-right">Custo Total</TableHead>
                  <TableHead className="text-right">Margem</TableHead>
                  <TableHead className="w-24 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSheets.map((sheet) => {
                  const calc = calculations[sheet.id];
                  const totalCost = calc 
                    ? calc.total_variable_expenses + calc.total_unit_cost 
                    : 0;
                  const isProfitable = calc?.is_profitable ?? true;
                  
                  return (
                    <TableRow key={sheet.id} data-testid={`sheet-row-${sheet.id}`}>
                      <TableCell>
                        <Link 
                          to={`/cost-sheets/${sheet.id}`}
                          className="font-medium text-slate-900 hover:text-teal-600"
                        >
                          {sheet.name}
                        </Link>
                        {sheet.description && (
                          <p className="text-sm text-slate-500">{sheet.description}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-xs">
                          {sheet.service_type === 'servico' ? 'Serviço' : 'Produto'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(sheet.sale_price)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(totalCost)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isProfitable ? (
                            <TrendingUp className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-rose-500" />
                          )}
                          <span className={`font-mono font-medium px-2 py-1 rounded-md text-sm ${
                            isProfitable 
                              ? 'bg-emerald-50 text-emerald-700' 
                              : 'bg-rose-50 text-rose-700'
                          }`}>
                            {formatPercent(calc?.contribution_margin_percent || 0)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/cost-sheets/${sheet.id}`}>
                            <Button variant="ghost" size="icon" data-testid={`edit-sheet-${sheet.id}`}>
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedSheet(sheet);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-sheet-${sheet.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Tem certeza que deseja excluir a ficha de custo <strong>{selectedSheet?.name}</strong>?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
